import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component,
    ElementRef, HostListener, OnDestroy, OnInit, ViewChild
} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {HorizontalSkewService} from './horizontal-skew.service';
import {HorizontalSkewChartPopupComponent} from './horizontal-skew-chart-popup.component';
import {DetectMethodChanges} from '../utils';

@Component({
    selector: 'ets-horizontal-skew-tray-indicator',
    templateUrl: './horizontal-skew-tray-indicator.component.html',
    styleUrls: ['./horizontal-skew-tray-indicator.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HorizontalSkewTrayIndicatorComponent implements OnInit, OnDestroy {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _service: HorizontalSkewService,
        private readonly _elementRef: ElementRef
    ) {
    }

    private _unsubscriber = new Subject<void>();

    @ViewChild(HorizontalSkewChartPopupComponent)
    chartPopup: HorizontalSkewChartPopupComponent;

    overlayVisible = false;
    overlayStyle: { [key: string]: string } = {};

    // Spot/Forward radio group (dx-radio-group items).
    readonly basisItems = [
        {text: 'Spot', value: 'spot'},
        {text: 'Forward', value: 'forward'},
    ];

    // Cheat-sheet, ordered high → low like a thermometer (Backwardation at the top).
    readonly bands = [
        {
            key: 'backwardation', label: 'Backwardation ( > 1.0 )',
            meaning: 'Short-term insurance costs more than long-term — the market is paying up for immediate protection.'
        },
        {
            key: 'transitioning', label: 'Transitioning ( = 1.0 )',
            meaning: 'The market is evenly balanced across near and far.'
        },
        {
            key: 'contango', label: 'Contango ( < 1.0 )',
            meaning: 'Long-term insurance costs more than short-term — the normal order of things.'
        },
    ];

    get available(): boolean {
        return this._service.ratio != null;
    }

    get loading(): boolean {
        return this._service.loading;
    }

    get displayRatio(): string {
        return this._service.ratio != null ? this._service.ratio.toFixed(2) : 'N/A';
    }

    get short(): number {
        return this._service.short;
    }

    get long(): number {
        return this._service.long;
    }

    get basis(): string {
        return this._service.basis;
    }

    get band(): string | null {
        return this._service.band;
    }

    get shortLegLabel(): string | null {
        return this._service.shortLegLabel;
    }

    get longLegLabel(): string | null {
        return this._service.longLegLabel;
    }

    ngOnInit(): void {
        this._service.init('SPX', 'hskew.tray');

        this._service.updated$
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(() => this._changeDetector.detectChanges());
    }

    ngOnDestroy(): void {
        this._unsubscriber.next();
        this._unsubscriber.complete();
        this._service.dispose();
    }

    colorClass(): string {
        const b = this._service.band;
        if (b == null) { return 'hs-na'; }
        if (b === 'backwardation') { return 'hs-red'; }
        if (b === 'contango') { return 'hs-blue'; }
        return 'hs-neutral';
    }

    isBandActive(key: string): boolean {
        return this._service.band === key;
    }

    @DetectMethodChanges()
    toggleOverlay(): void {
        this.overlayVisible = !this.overlayVisible;
        if (this.overlayVisible) {
            const box = this._elementRef.nativeElement.querySelector('.hs-box');
            if (box) {
                const rect = box.getBoundingClientRect();
                this.overlayStyle = {
                    left: (rect.left - 150) + 'px',
                    bottom: (window.innerHeight - rect.top + 4) + 'px'
                };
            }
        }
    }

    /** Chart button (top-right of the overlay): open the standalone term-structure chart. */
    openChart(): void {
        const underlying = this._service.underlying;
        if (!underlying || !this.chartPopup) { return; }
        // The chart reads the surface's CURRENT basis — it has no switcher of its own.
        this.chartPopup.open(underlying, this._service.basis);
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.overlayVisible) { return; }
        if (this._elementRef.nativeElement.contains(event.target)) { return; }
        // DevExtreme editors teleport their popups to <body> (.dx-overlay-wrapper) —
        // clicks in them are logically inside this overlay, physically outside it.
        const el = event.target as Element;
        if (el && el.closest && el.closest('.dx-overlay-wrapper')) { return; }
        this.overlayVisible = false;
        this._changeDetector.detectChanges();
    }

    // DX fires onValueChanged for programmatic [value] updates too — `$event.event`
    // discriminates a USER entry (programmatic re-resolves must not loop back in).
    @DetectMethodChanges({isAsync: true})
    async onShortChanged(e: any): Promise<void> {
        if (!e || !e.event) { return; }
        const applied = await this._service.setLegs(e.value, this._service.long);
        // A rejected entry (e.g. short >= long) left the widget showing a value the
        // service refused; the unchanged [value] binding can't correct it — write the
        // truth back (the programmatic set fires onValueChanged without e.event).
        if (!applied && e.component) {
            e.component.option('value', this._service.short);
        }
    }

    @DetectMethodChanges({isAsync: true})
    async onLongChanged(e: any): Promise<void> {
        if (!e || !e.event) { return; }
        const applied = await this._service.setLegs(this._service.short, e.value);
        if (!applied && e.component) {
            e.component.option('value', this._service.long);
        }
    }

    @DetectMethodChanges({isAsync: true})
    async onBasisChanged(e: any): Promise<void> {
        if (!e || !e.event) { return; }
        await this._service.setBasis(e.value);
    }
}
